import { useEffect, useRef } from "react"

import consumer from "@/channels/consumer"
import type { Flashcard } from "@/types"

type FlashcardUpdateCallback = (flashcard: Flashcard) => void

interface FlashcardChannelMessage {
  type: "audio_ready" | "audio_failed"
  flashcard: Flashcard
}

/**
 * Hook for subscribing to FlashcardChannel (user-scoped).
 * Receives real-time updates when flashcard audio generation completes.
 *
 * @param onFlashcardUpdate - Callback invoked when a flashcard is updated
 */
export function useFlashcardChannel(
  onFlashcardUpdate: FlashcardUpdateCallback,
) {
  const callbackRef = useRef(onFlashcardUpdate)

  // Keep callback ref current to avoid stale closures
  useEffect(() => {
    callbackRef.current = onFlashcardUpdate
  }, [onFlashcardUpdate])

  useEffect(() => {
    const subscription = consumer.subscriptions.create(
      { channel: "FlashcardChannel" },
      {
        connected() {
          // Successfully connected to FlashcardChannel
        },
        disconnected() {
          // Disconnected from FlashcardChannel
        },
        received(data: FlashcardChannelMessage) {
          if (data.type === "audio_ready" || data.type === "audio_failed") {
            callbackRef.current(data.flashcard)
          }
        },
      },
    )

    return () => {
      subscription.unsubscribe()
    }
  }, [])
}
